import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CodeIcon from '@mui/icons-material/Code';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AssessmentIcon from '@mui/icons-material/Assessment';
import type { AttemptState, CriteriaScores } from '../../types';
import { ValidationStatusBadge } from './ValidationStatusBadge';
import { CriteriaScoreBar } from './CriteriaScoreBar';

const CRITERIA_LABELS: { key: keyof CriteriaScores; label: string }[] = [
  { key: 'cefr_alignment', label: 'CEFR Alignment' },
  { key: 'pedagogical_soundness', label: 'Pedagogical Soundness' },
  { key: 'student_centeredness', label: 'Student-Centredness' },
  { key: 'clarity_practicality', label: 'Clarity & Practicality' },
  { key: 'online_appropriateness', label: 'Online Appropriateness' },
  { key: 'completeness', label: 'Completeness' },
];

interface Props {
  attempt: AttemptState;
  previousAttempt?: AttemptState | null;
  isActive: boolean;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}

// Uses a Box instead of MUI Button so the expand icon can be reliably
// pushed to the right edge with ml:'auto', regardless of title length.
const CollapsibleSection: React.FC<SectionProps> = ({
  icon,
  title,
  defaultOpen = false,
  children,
  badge,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.75,
          cursor: 'pointer',
          color: 'text.secondary',
          userSelect: 'none',
          borderRadius: 0.5,
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
          px: 0.5,
          mx: -0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 14, flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        {badge && <Box sx={{ flexShrink: 0 }}>{badge}</Box>}
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ mb: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

const RawPromptBlock: React.FC<{ system: string; user: string }> = ({ system, user }) => {
  const [which, setWhich] = useState<'system' | 'user'>('user');

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
        <Chip
          label="User"
          size="small"
          variant={which === 'user' ? 'filled' : 'outlined'}
          onClick={() => setWhich('user')}
          sx={{ fontSize: 10, cursor: 'pointer' }}
        />
        <Chip
          label="System"
          size="small"
          variant={which === 'system' ? 'filled' : 'outlined'}
          onClick={() => setWhich('system')}
          sx={{ fontSize: 10, cursor: 'pointer' }}
        />
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: 'grey.900',
          color: 'grey.100',
          borderRadius: 1,
          fontSize: 10,
          fontFamily: 'monospace',
          overflowX: 'auto',
          maxHeight: 220,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {which === 'user' ? user : system}
      </Box>
    </Box>
  );
};

export const AttemptCard: React.FC<Props> = ({ attempt, previousAttempt, isActive }) => {
  const isLoading = attempt.isGenerating || attempt.isValidating;
  const hasScratchpad = Boolean(attempt.scratchpad);
  const hasScores = Boolean(attempt.scores);
  const hasGeneratorContent = Boolean(attempt.generatorPrompt || attempt.generatorResponse);
  const hasValidatorContent = Boolean(attempt.validatorPrompt || hasScratchpad || hasScores);

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: isActive ? 'primary.main' : 'divider',
        borderWidth: isActive ? 2 : 1,
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: isActive ? 'primary.50' : 'grey.50',
          borderRadius: '7px 7px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          Attempt {attempt.attemptNumber}
        </Typography>

        {isActive && isLoading && (
          <Chip
            label={attempt.isGenerating ? 'Generating…' : 'Validating…'}
            size="small"
            color="primary"
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
        )}

        {attempt.decision && (
          <ValidationStatusBadge decision={attempt.decision} score={attempt.weightedScore} />
        )}
      </Box>

      {/* ── Loading bar ── */}
      {isLoading && <LinearProgress />}

      {/* ── Body ── */}
      <Box sx={{ px: 2, pt: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>

        {/* Generator section */}
        {attempt.generatorPrompt && (
          <CollapsibleSection
            icon={<CodeIcon sx={{ fontSize: 14 }} />}
            title="Generator Prompt"
          >
            <RawPromptBlock
              system={attempt.generatorPrompt.systemPrompt}
              user={attempt.generatorPrompt.userPrompt}
            />
          </CollapsibleSection>
        )}

        {attempt.generatorResponse && (
          <CollapsibleSection
            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            title="Generator Response"
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                mt: 0.5,
                bgcolor: 'grey.900',
                color: 'grey.100',
                borderRadius: 1,
                fontSize: 10,
                fontFamily: 'monospace',
                overflowX: 'auto',
                maxHeight: 200,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {attempt.generatorResponse}
            </Box>
          </CollapsibleSection>
        )}

        {/* Divider only shown when there is content on both sides */}
        {hasGeneratorContent && hasValidatorContent && (
          <Divider sx={{ my: 0.5 }} />
        )}

        {/* Validator section */}
        {attempt.validatorPrompt && (
          <CollapsibleSection
            icon={<CodeIcon sx={{ fontSize: 14 }} />}
            title="Validator Prompt"
          >
            <RawPromptBlock
              system={attempt.validatorPrompt.systemPrompt}
              user={attempt.validatorPrompt.userPrompt}
            />
          </CollapsibleSection>
        )}

        {hasScratchpad && (
          <CollapsibleSection
            icon={<PsychologyIcon sx={{ fontSize: 14 }} />}
            title="Validator Reasoning"
            defaultOpen
          >
            <Box
              sx={{
                mt: 0.5,
                p: 1.5,
                bgcolor: 'info.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'info.200',
                maxHeight: 260,
                overflowY: 'auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{ display: 'block', whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'text.primary' }}
              >
                {attempt.scratchpad}
              </Typography>
            </Box>
          </CollapsibleSection>
        )}

        {hasScores && attempt.scores && (
          <CollapsibleSection
            icon={<AssessmentIcon sx={{ fontSize: 14 }} />}
            title="Criteria Scores"
            defaultOpen
          >
            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              {CRITERIA_LABELS.map(({ key, label }) => {
                // eslint-disable-next-line security/detect-object-injection
                const score = attempt.scores ? attempt.scores[key] : 0;
                // eslint-disable-next-line security/detect-object-injection
                const prevScore = previousAttempt?.scores?.[key] ?? null;
                return (
                  <CriteriaScoreBar key={key} label={label} score={score} previousScore={prevScore} />
                );
              })}

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Weighted Score
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {attempt.weightedScore?.toFixed(1)}/100
                </Typography>
                {attempt.decision && <ValidationStatusBadge decision={attempt.decision} />}
              </Box>
            </Box>
          </CollapsibleSection>
        )}

        {/* Guided feedback for next attempt */}
        {attempt.guidedFeedback && (
          <Tooltip title="This feedback was injected into the next generation attempt" placement="top">
            <Alert severity="warning" sx={{ fontSize: 11, py: 0.5, mt: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                Guided feedback for next attempt:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                {attempt.guidedFeedback}
              </Typography>
            </Alert>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};
